import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "services/api";
import ls from "services/ls";
import emailValidate from "services/emailValidate";
import Translator, { __ } from "services/Translator";
import SignupBonusDropdown from "components/SignupBonusDropdown";
import PhoneInput from "components/PhoneInput";
import CountrySelect from "components/CountrySelect";
import DatePicker from "elements/DatePicker";
import {
  sanitizeTextInput,
  validateDateOfBirth,
  validateCity,
  validateStreetAddress,
  validatePostalCode,
  normalizePostalCode,
  INPUT_FILTERS,
} from "utils/profileValidation";
import { completeSignupFlow, noteSignupInput } from "utils/signupAbandonment";
import {
  filterCuratedCampaigns,
  isBonusDropdownEnabled,
  getSignupPrefill,
  getAllowedCurrencies,
  getCurrencyForCountry,
} from "utils/signupFlow";

/**
 * Password sign-up form for the dynamic presets 2–5 (email+password, +DOB,
 * +phone, +nickname/address), rendered inside the streamlined (betmode) modal.
 *
 * The field set + order is driven entirely by `presetFields` (which comes from
 * config via signup_flow), so this component never decides on its own which
 * fields a preset collects. Passwordless (preset 1) is handled elsewhere and is
 * untouched. Posts to the existing /quick-signup endpoint; the backend enforces
 * the same preset field-set server-side.
 */

function toYmd(date) {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return d.getFullYear() + "-" + m + "-" + day;
}

function maxDob() {
  const d = new Date();
  return new Date(d.getFullYear() - 18, d.getMonth(), d.getDate());
}

export default function PasswordPresetForm({
  presetFields = ["email", "password"],
  initialSettings = {},
  referralCode,
  onSuccess,
  setSpinner,
  closeLoginModal,
}) {
  const selectedLanguage = Translator.getSelectedLanguage();

  const want = useMemo(
    () => ({
      nickname: presetFields.includes("nickname"),
      dob: presetFields.includes("date_of_birth"),
      phone: presetFields.includes("phone"),
      address: presetFields.includes("address"),
      country: presetFields.includes("country"),
      currency: presetFields.includes("currency"),
    }),
    [presetFields]
  );

  const bonusEnabled = isBonusDropdownEnabled(initialSettings);
  const prefill = getSignupPrefill(initialSettings);
  const allowedCurrencies = getAllowedCurrencies(initialSettings);

  const [showPass, setShowPass] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [errors, setErrors] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [isCampaignsLoading, setIsCampaignsLoading] = useState(true);
  const [hasManualBonusSelection, setHasManualBonusSelection] = useState(false);

  const [form, setForm] = useState({
    nickName: "",
    email: "",
    password: "",
    streetAddress: "",
    city: "",
    postalCode: "",
    bonusOffer: "no_bonus",
    agreeToTerms: false,
    ageConfirmation: false,
  });
  const [dob, setDob] = useState(null);
  const [phoneInfo, setPhoneInfo] = useState({ e164: "", isValid: false });
  const [country, setCountry] = useState(prefill.country || null);
  const [currency, setCurrency] = useState(prefill.currency || "");
  // True once CountrySelect has matched `country` against its own /countries
  // list. The IP pre-fill is NOT intersected with that list, so a blocked
  // country would otherwise sit in state while the button reads "Select
  // country" — and only fail at submit.
  const [countryInList, setCountryInList] = useState(false);
  const currencyTouchedRef = useRef(false);
  // Address is entered as street + house number, combined on submit. State/
  // province is loaded per country and only shown when the country has one.
  const [streetName, setStreetName] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [states, setStates] = useState([]);

  // Apply IP pre-fill once, if the config arrives after mount and the player has
  // not already set a value (keeps the choice overridable).
  const prefillAppliedRef = useRef(false);
  useEffect(() => {
    if (prefillAppliedRef.current) return;
    if (prefill.country || prefill.currency) {
      if (prefill.country) setCountry((c) => c || prefill.country);
      if (prefill.currency) setCurrency((c) => c || prefill.currency);
      prefillAppliedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill.country, prefill.currency]);

  // Load the state/province list for the chosen country. Empty result => the
  // country has none, so the field is hidden (dynamic per country).
  useEffect(() => {
    if (!country) {
      setStates([]);
      return;
    }
    let alive = true;
    setStateVal("");
    api.get("/states/" + country).then((r) => {
      if (alive) setStates(r.status === 200 && Array.isArray(r.data) ? r.data : []);
    });
    return () => {
      alive = false;
    };
  }, [country]);

  const curatedCampaigns = filterCuratedCampaigns(campaigns, initialSettings);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  useEffect(() => {
    if (!bonusEnabled) return;
    let cancelled = false;
    (async () => {
      setIsCampaignsLoading(true);
      try {
        const res = await api.post("/get-registration-campaigns", {
          language: selectedLanguage?.code,
          currency: "USD",
        });
        if (!cancelled && res.status === 200) {
          setCampaigns(Array.isArray(res.data) ? res.data : []);
        }
      } finally {
        if (!cancelled) setIsCampaignsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bonusEnabled, selectedLanguage?.code]);

  useEffect(() => {
    if (hasManualBonusSelection || isCampaignsLoading) return;
    if (!bonusEnabled || !curatedCampaigns.length) {
      setField("bonusOffer", "no_bonus");
      return;
    }
    setField("bonusOffer", curatedCampaigns[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaigns, initialSettings, hasManualBonusSelection, isCampaignsLoading]);

  const handleBonusSelection = (value) => {
    setField("bonusOffer", value);
    setHasManualBonusSelection(true);
  };

  // Country and currency move together: the currency follows the country the
  // same way the backend fills the pair, until the player picks one explicitly.
  const handleCountryChange = (c) => {
    const code = c?.code || c || null;
    setCountry(code);
    setCountryInList(Boolean(code));
    setErrors((prev) => ({ ...prev, country: "" }));
    if (!currencyTouchedRef.current) {
      const next = getCurrencyForCountry(initialSettings, code);
      if (next) {
        setCurrency(next);
        setErrors((prev) => ({ ...prev, currency: "" }));
      }
    }
  };

  const validate = () => {
    const e = {};
    const email = sanitizeTextInput(form.email, 250).toLowerCase();
    if (!emailValidate(email)) e.email = __("Please enter a valid email address");
    if (form.password.length < 8 || form.password.length > 30) {
      e.password = __("Your password must contain between 8 and 30 characters");
    }
    if (want.nickname) {
      const n = sanitizeTextInput(form.nickName, 30);
      if (n.length < 2 || n.length > 30) e.nickName = __("Please enter a nickname (2 to 30 characters).");
    }
    if (want.dob) {
      const dobCheck = validateDateOfBirth(dob);
      if (!dobCheck.valid) e.dateOfBirth = dobCheck.error;
    }
    if (want.phone && (!phoneInfo.isValid || !phoneInfo.e164)) {
      e.phone = __("Please enter a valid phone number.");
    }
    if (want.address) {
      const rs = validateStreetAddress(streetName);
      if (!rs.valid) e.address = rs.error;
      else if (!houseNumber.trim()) e.address = __("Please enter your house number.");
      const rc = validateCity(form.city);
      if (!rc.valid) e.city = rc.error;
      const rz = validatePostalCode(form.postalCode, country);
      if (!rz.valid) e.postalCode = rz.error;
    }
    if (want.country || want.address) {
      if (!country) e.country = __("Please select your country.");
      else if (!countryInList) e.country = __("Please choose your country from the list.");
    }
    if (want.currency && !currency) {
      e.currency = __("Please select your currency.");
    }
    if (!form.ageConfirmation) e.ageConfirmation = __("Please check the age confirmation.");
    if (!form.agreeToTerms) e.agreeToTerms = __("Please check the terms and conditions.");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (isRegistering) return;
    if (!validate()) return;

    setIsRegistering(true);
    setSpinner?.(true);

    const payload = {
      email: sanitizeTextInput(form.email, 250).toLowerCase(),
      password: form.password,
      bonusOffer: bonusEnabled ? form.bonusOffer : "no_bonus",
      referralCode: sanitizeTextInput(referralCode, 80),
      agreeToTerms: form.agreeToTerms ? 1 : 0,
      ageConfirmation: form.ageConfirmation ? 1 : 0,
      language: selectedLanguage?.code,
    };
    if (want.nickname) payload.nickName = sanitizeTextInput(form.nickName, 30);
    if (want.dob) payload.dateOfBirth = toYmd(dob);
    if (want.phone) payload.phone = phoneInfo.e164;
    if (want.address) {
      payload.streetAddress = (sanitizeTextInput(streetName, 190) + " " + houseNumber.trim()).trim();
      payload.city = sanitizeTextInput(form.city, 100);
      payload.country = country;
      payload.postalCode = sanitizeTextInput(form.postalCode, 20);
      if (stateVal) payload.state = stateVal;
    }
    if (want.country && !want.address) payload.country = country;
    if (want.currency) payload.currency = currency;

    const affToken = ls.get("afftoken");
    if (affToken) payload.affiliateToken = affToken;

    try {
      const response = await api.post("/quick-signup", payload);
      if (response.status === 200) {
        // Signed up: never chase this visitor with the abandonment pop-up, and
        // never let the post-signup pop-up land on top of it.
        completeSignupFlow();
        onSuccess?.(payload.email, payload.password);
      } else if (response.data?.errors) {
        setErrors(response.data.errors);
      } else {
        setErrors({ form: response.data?.message || __("Registration failed") });
      }
    } catch (err) {
      setErrors({ form: err?.message || __("Registration failed") });
    } finally {
      setSpinner?.(false);
      setIsRegistering(false);
    }
  };

  const fieldError = (key) =>
    errors[key] ? <div className="betmode-auth__error">{errors[key]}</div> : null;

  const countryField = () => (
    <div className="betmode-auth__field">
      <label className="betmode-auth__label">{__("Country")}</label>
      <CountrySelect
        selected={country}
        onChange={handleCountryChange}
        onUnavailable={() => setCountryInList(false)}
        placeholder={__("Search country...")}
        variant="country"
      />
      {country && !countryInList ? (
        <div className="betmode-auth__error">
          {__("We could not use your detected location because it is not available here. Please choose your country from the list.")}
        </div>
      ) : null}
      {fieldError("country")}
    </div>
  );

  const dobField = want.dob ? (
    <div className="betmode-auth__field">
      <label className="betmode-auth__label" htmlFor="pp-dob">{__("Date of birth")}</label>
      <DatePicker
        value={dob}
        onChange={(date) => {
          setDob(date);
          setErrors((prev) => ({ ...prev, dateOfBirth: "" }));
        }}
        maxDate={maxDob()}
        showYearDropdown
        scrollableYearDropdown
        yearDropdownItemNumber={100}
        dateFormat="yyyy-MM-dd"
        placeholderText={__("Select your date of birth")}
        className={"betmode-auth__input " + (errors.dateOfBirth ? "betmode-auth__input--error" : "")}
      />
      {fieldError("dateOfBirth")}
    </div>
  ) : null;

  const currencyField = want.currency ? (
    <div className="betmode-auth__field">
      <label className="betmode-auth__label" htmlFor="pp-currency">{__("Currency")}</label>
      <select
        id="pp-currency"
        className={"betmode-auth__input " + (errors.currency ? "betmode-auth__input--error" : "")}
        value={currency}
        onChange={(e) => {
          currencyTouchedRef.current = true;
          setCurrency(e.target.value);
          setErrors((prev) => ({ ...prev, currency: "" }));
        }}
      >
        <option value="">{__("Select currency")}</option>
        {allowedCurrencies.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </select>
      {fieldError("currency")}
    </div>
  ) : null;

  return (
    <form className="betmode-auth__form" autoComplete="off" onSubmit={submit}>
      {errors.form ? <div className="betmode-auth__error">{errors.form}</div> : null}

      {want.nickname ? (
        <div className="betmode-auth__field">
          <label className="betmode-auth__label" htmlFor="pp-nick">{__("Nickname")}</label>
          <input
            id="pp-nick"
            type="text"
            className={"betmode-auth__input " + (errors.nickName ? "betmode-auth__input--error" : "")}
            value={form.nickName}
            onChange={(e) => setField("nickName", sanitizeTextInput(e.target.value, 30))}
            placeholder={__("Nickname")}
            autoComplete="off"
          />
          {fieldError("nickName")}
        </div>
      ) : null}

      <div className="betmode-auth__field">
        <label className="betmode-auth__label" htmlFor="pp-email">{__("Your email")}</label>
        <input
          id="pp-email"
          type="email"
          className={"betmode-auth__input " + (errors.email ? "betmode-auth__input--error" : "")}
          value={form.email}
          onChange={(e) => {
            const next = sanitizeTextInput(e.target.value, 250);
            noteSignupInput(Boolean(next.trim()));
            setField("email", next);
          }}
          placeholder={__("Email")}
          autoComplete="email"
        />
        {fieldError("email")}
      </div>

      <div className="betmode-auth__field betmode-auth__field--password">
        <label className="betmode-auth__label" htmlFor="pp-pass">{__("Password")}</label>
        <input
          id="pp-pass"
          type={showPass ? "text" : "password"}
          className={"betmode-auth__input " + (errors.password ? "betmode-auth__input--error" : "")}
          value={form.password}
          onChange={(e) => setField("password", String(e.target.value).slice(0, 30))}
          placeholder={__("Enter Password")}
          autoComplete="new-password"
        />
        <i
          className={"betmode-auth__pass-toggle fa " + (showPass ? "fa-eye-slash" : "fa-eye")}
          onClick={() => setShowPass((v) => !v)}
          aria-hidden="true"
        />
        {fieldError("password")}
      </div>

      {want.dob && want.currency ? (
        // Date of birth + Currency paired on one two-column row (cashier layout).
        <div className="betmode-auth__row2">
          {dobField}
          {currencyField}
        </div>
      ) : (
        dobField
      )}

      {want.phone ? (
        <div className="betmode-auth__field">
          <label className="betmode-auth__label">{__("Phone number")}</label>
          <PhoneInput
            defaultCountry={country || "US"}
            value={phoneInfo.e164}
            onChange={(info) => {
              setPhoneInfo(info);
              setErrors((prev) => ({ ...prev, phone: "" }));
            }}
            placeholder={__("Mobile number")}
            containerClassName="phone_input_container"
          />
          {fieldError("phone")}
        </div>
      ) : null}

      {want.address ? (
        <>
          {countryField()}
          <div className="betmode-auth__field">
            <label className="betmode-auth__label" htmlFor="pp-street">{__("Street")}</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                id="pp-street"
                type="text"
                style={{ flex: 2 }}
                className={"betmode-auth__input " + (errors.address ? "betmode-auth__input--error" : "")}
                value={streetName}
                onChange={(e) => {
                  setStreetName(INPUT_FILTERS.street(e.target.value));
                  setErrors((prev) => ({ ...prev, address: "" }));
                }}
                placeholder={__("Street name")}
                autoComplete="address-line1"
              />
              <input
                type="text"
                style={{ flex: 1 }}
                className={"betmode-auth__input " + (errors.address ? "betmode-auth__input--error" : "")}
                value={houseNumber}
                onChange={(e) => {
                  setHouseNumber(INPUT_FILTERS.house(e.target.value));
                  setErrors((prev) => ({ ...prev, address: "" }));
                }}
                placeholder={__("85c")}
                autoComplete="address-line2"
              />
            </div>
            {fieldError("address")}
          </div>
          <div className="betmode-auth__field">
            <label className="betmode-auth__label" htmlFor="pp-city">{__("City")}</label>
            <input
              id="pp-city"
              type="text"
              className={"betmode-auth__input " + (errors.city ? "betmode-auth__input--error" : "")}
              value={form.city}
              onChange={(e) => setField("city", INPUT_FILTERS.city(e.target.value))}
              placeholder={__("City")}
              autoComplete="address-level2"
            />
            {fieldError("city")}
          </div>
          {states.length > 0 ? (
            <div className="betmode-auth__field">
              <label className="betmode-auth__label" htmlFor="pp-state">{__("State / Province")}</label>
              <select
                id="pp-state"
                className="betmode-auth__input"
                value={stateVal}
                onChange={(e) => setStateVal(e.target.value)}
              >
                <option value="">{__("Select")}</option>
                {states.map((s) => (
                  <option key={s.id} value={s.iso2 || s.name}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="betmode-auth__field">
            <label className="betmode-auth__label" htmlFor="pp-zip">{__("Postal code")}</label>
            <input
              id="pp-zip"
              type="text"
              className={"betmode-auth__input " + (errors.postalCode ? "betmode-auth__input--error" : "")}
              value={form.postalCode}
              onChange={(e) => setField("postalCode", INPUT_FILTERS.zip(e.target.value))}
              onBlur={() => setField("postalCode", normalizePostalCode(form.postalCode, country))}
              placeholder={__("Postal code")}
              autoComplete="postal-code"
            />
            {fieldError("postalCode")}
          </div>
        </>
      ) : null}

      {want.country && !want.address ? countryField() : null}

      {want.currency && !want.dob ? currencyField : null}

      {bonusEnabled ? (
        <div className="betmode-auth__field betmode-auth__field--bonus">
          <label className="betmode-auth__label">{__("Choose your bonus")}</label>
          <SignupBonusDropdown
            campaigns={curatedCampaigns}
            isCampaignsLoading={isCampaignsLoading}
            bonusOffer={form.bonusOffer}
            onSelectBonus={handleBonusSelection}
          />
        </div>
      ) : null}

      <div className="betmode-auth__checks">
        <label className="betmode-auth__check">
          <input
            type="checkbox"
            checked={Boolean(form.ageConfirmation && form.agreeToTerms)}
            onChange={(e) => {
              const v = e.target.checked;
              setForm((prev) => ({ ...prev, ageConfirmation: v, agreeToTerms: v }));
              setErrors((prev) => ({ ...prev, ageConfirmation: "", agreeToTerms: "" }));
            }}
          />
          <span>
            {__(
              "I confirm I am 18 years or older, located in a permitted territory with no self-exclusions, and I have read and accept the"
            ) + " "}
            <Link to="/terms-and-conditions" onClick={closeLoginModal} rel="nofollow noopener noreferrer">
              {__("Terms of service")}
            </Link>{" "}
            {__("and any other policies.")}
          </span>
        </label>
        {fieldError("ageConfirmation") || fieldError("agreeToTerms")}
      </div>

      <button type="submit" className="betmode-auth__submit" disabled={isRegistering}>
        {__("Play Now")}
      </button>
    </form>
  );
}
